/**
 * GraphQL Queries and Mutations for Defects
 * Mirrors the REST API functions for defects from evirApi.js
 */

import { gql } from '@apollo/client';
import { evirPxClient as client } from './graphqlClient';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

/**
 * Search defects with filters
 * companyId is inferred by the backend from the user's auth token
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} Response with defects data and headers
 */
export const getDefectList = async (params) => {
	const {
		page = 1,
		perPage = 25,
		sort = 'severity',
		sortDir = 'desc',
		dates,
		statuses,
		pxAssetIds,
	} = params;

	const query = gql`
		query SearchDefects(
			$conditions: [DefectSearchInput]
			$sort: [DefectSortInput]
			$limit: Int
			$offset: Int
		) {
			searchDefects(
				conditions: $conditions
				sort: $sort
				limit: $limit
				offset: $offset
			) {
				items {
					id
					inspectionId
					inspection {
						id
						name
						startTime
					}
					assetId
					asset {
						id
						label
						licensePlate
						vin
					}
					driverId
					driver {
						id
						name
					}
					legacyId
					draft
					firstReported
					lastReported
					severity
					status
					layoutId
					typeId
					inspName
					zone
					component
					condition
					comment
					repairs {
						createdOn
						status
						data
					}
					data
					createdOn
					createdBy
					updatedOn
					updatedBy
				}
				total
			}
		}
	`;

	// Sort mapping
	const sortFieldMap = {
		severity: 'severity',
		lastReported: 'lastReported',
		status: 'status',
		assetLabel: 'asset_label',
	};

	// DefectSearchInput doesn't support range or fields/terms/exact filters
	// Fetch all defects and filter client-side
	const { data } = await client.query({
		query,
		variables: {
			conditions: [], // No server-side filtering supported
			sort: [
				{
					field: sortFieldMap[sort] || 'severity',
					order: sortDir.toUpperCase(),
				},
			],
			limit: 500, // Fetch more to filter client-side
			offset: 0,
		},
	});

	let filteredDefects = data.searchDefects?.items || [];

	// Filter by date range client-side
	if (dates && dates.length > 0) {
		const { startTime, endTime } = dates[0];
		if (startTime && endTime) {
			const startDate = new Date(startTime);
			const endDate = new Date(endTime);
			filteredDefects = filteredDefects.filter((defect) => {
				const defectDate = new Date(defect.lastReported || defect.firstReported);
				return defectDate >= startDate && defectDate <= endDate;
			});
		}
	}

	// Filter by status client-side
	if (statuses && statuses.length > 0) {
		filteredDefects = filteredDefects.filter((defect) =>
			statuses.includes(defect.status)
		);
	}

	// Filter by asset IDs client-side
	if (pxAssetIds && pxAssetIds.length > 0) {
		const assetIdSet = new Set(pxAssetIds.map(Number));
		filteredDefects = filteredDefects.filter((defect) =>
			assetIdSet.has(Number(defect.assetId))
		);
	}

	// Apply pagination
	const total = filteredDefects.length;
	const startIndex = (page - 1) * perPage;
	const paginatedDefects = filteredDefects.slice(startIndex, startIndex + perPage);

	// Return format matching REST API
	return {
		data: paginatedDefects,
		headers: {
			'x-total-count': total,
		},
	};
};

/**
 * Get defects for a specific asset
 * Fetches defects and filters client-side by assetId since the GraphQL schema
 * doesn't support the fields/terms/exact filter format for DefectSearchInput.
 * companyId is inferred by the backend from the user's auth token
 * @param {string} assetId - Asset ID
 * @param {Object} params - Optional query parameters
 * @returns {Promise<Object>} Response with defects data and headers
 */
export const fetchAssetDefects = async (assetId, params = {}) => {
	const {
		page = 1,
		perPage = 25,
		sort = 'severity',
		sortDir = 'desc',
		statuses,
		dates,
	} = params;

	const query = gql`
		query SearchAssetDefects(
			$conditions: [DefectSearchInput]
			$sort: [DefectSortInput]
			$limit: Int
			$offset: Int
		) {
			searchDefects(
				conditions: $conditions
				sort: $sort
				limit: $limit
				offset: $offset
			) {
				items {
					id
					inspectionId
					inspection {
						id
						name
						startTime
					}
					assetId
					asset {
						id
						label
						licensePlate
						vin
					}
					driverId
					driver {
						id
						name
					}
					firstReported
					lastReported
					severity
					status
					layoutId
					typeId
					inspName
					zone
					component
					condition
					comment
					repairs {
						createdOn
						status
						data
					}
					data
					createdOn
					updatedOn
				}
				total
			}
		}
	`;

	// DefectSearchInput doesn't support range or fields/terms/exact filters
	// Fetch all defects and filter client-side
	const { data } = await client.query({
		query,
		variables: {
			conditions: [], // No server-side filtering supported
			sort: [
				{
					field: sort,
					order: sortDir.toUpperCase(),
				},
			],
			limit: 500, // Fetch more to filter client-side
			offset: 0,
		},
	});

	// Filter client-side by assetId
	const targetAssetId = Number(assetId);
	let filteredDefects = (data.searchDefects?.items || []).filter(
		(defect) => defect.assetId === targetAssetId || Number(defect.assetId) === targetAssetId
	);

	// Filter by date range client-side
	if (dates && dates.length > 0) {
		const { startTime, endTime } = dates[0];
		if (startTime && endTime) {
			const startDate = new Date(startTime);
			const endDate = new Date(endTime);
			filteredDefects = filteredDefects.filter((defect) => {
				const defectDate = new Date(defect.lastReported || defect.firstReported);
				return defectDate >= startDate && defectDate <= endDate;
			});
		}
	}

	// Filter by status if provided
	if (statuses && statuses.length > 0) {
		filteredDefects = filteredDefects.filter(
			(defect) => statuses.includes(defect.status)
		);
	}

	// Apply pagination
	const total = filteredDefects.length;
	const startIndex = (page - 1) * perPage;
	const paginatedDefects = filteredDefects.slice(startIndex, startIndex + perPage);

	// Return format matching REST API
	return {
		data: paginatedDefects,
		headers: {
			'x-total-count': total,
		},
	};
};

/**
 * Create repair for a defect
 * @param {string} defectId - Defect ID
 * @param {Object} repairData - Repair information
 * @returns {Promise<Object>} Created repair data
 */
export const createDefectRepair = async (defectId, repairData) => {
	const mutation = gql`
		mutation CreateRepair(
			$id: ID!
			$inspectionId: String!
			$defectId: String!
			$status: String!
			$data: AWSJSON!
		) {
			createRepair(
				id: $id
				inspectionId: $inspectionId
				defectId: $defectId
				status: $status
				data: $data
			) {
				id
				inspectionId
				defectId
				status
				data
				createdOn
				createdBy
				updatedOn
				updatedBy
			}
		}
	`;

	// Transform repairData to match GraphQL mutation format
	const repairId = `repair_${defectId}_${Date.now()}`;
	const repairStatus = repairData.repairStatus || 'pending';

	const { data } = await client.mutate({
		mutation,
		variables: {
			id: repairId,
			inspectionId: repairData.inspectionId || '',
			defectId,
			status: repairStatus,
			data: {
				userId: repairData.userId,
				userFirstName: repairData.userFirstName,
				userLastName: repairData.userLastName,
				companyId: repairData.companyId,
				assetId: repairData.assetId,
				mechanicId: repairData.mechanicId,
				mechanicFirstName: repairData.mechanicFirstName,
				mechanicLastName: repairData.mechanicLastName,
				mechanicEmail: repairData.mechanicEmail,
				comment: repairData.comment,
				resolution: repairData.resolution,
				resolutionType: repairData.resolutionType,
				workOrder: repairData.workOrder,
			},
		},
	});

	return data.createRepair;
};

/**
 * Update defect status or details
 * @param {string} defectId - Defect ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated defect data
 */
export const updateDefect = async (defectId, updates) => {
	const mutation = gql`
		mutation UpdateDefect(
			$id: ID!
			$status: String
			$severity: Int
			$data: AWSJSON
		) {
			updateDefect(id: $id, status: $status, severity: $severity, data: $data) {
				id
				inspectionId
				assetId
				driverId
				status
				severity
				lastReported
				data
				updatedOn
				updatedBy
			}
		}
	`;

	const { data } = await client.mutate({
		mutation,
		variables: {
			id: defectId,
			...updates,
		},
	});

	return data.updateDefect;
};

/**
 * Get defect by ID
 * @param {string} defectId - Defect ID
 * @returns {Promise<Object>} Defect details
 */
export const getDefect = async (defectId) => {
	const query = gql`
		query GetDefect($id: String!) {
			getDefect(id: $id) {
				id
				inspectionId
				inspection {
					id
					name
					startTime
					typeId
				}
				assetId
				asset {
					id
					label
					licensePlate
					vin
					make
					model
					year
				}
				driverId
				driver {
					id
					name
					empNum
				}
				legacyId
				draft
				firstReported
				lastReported
				severity
				status
				layoutId
				typeId
				inspName
				zone
				component
				condition
				data
				createdOn
				createdBy
				updatedOn
				updatedBy
			}
		}
	`;

	const { data } = await client.query({
		query,
		variables: { id: defectId },
	});

	return data.getDefect;
};

export const fetchDefectPhotos = async (fileKeys) => {
	if (!fileKeys) {
		throw new Error('fileKeys parameter is required');
	}

	const urlEntries = await Promise.all(
		fileKeys.map(async (key) => {
			const url = await downloadAttachmentUrlByFileKey(key);
			return [key, url];
		})
	);

	return urlEntries.reduce((acc, [key, url]) => {
		if (url) {
			acc[key] = url;
		}
		return acc;
	}, {});
};

const downloadAttachmentUrlByFileKey = async (fileKey) => {
	if (!fileKey) {
		throw new Error('File key is required to download attachment URL');
	}

	try {
		const query = `
			query DownloadAttachmentUrlByFileKey($fileKey: String!) {
				downloadAttachmentUrlByFileKey(fileKey: $fileKey)
			}
		`;

		const data = await clientQuery(
			query,
			{ fileKey },
			endpoints.GRAPHQL_GENERAL_V2,
		);

		return data.downloadAttachmentUrlByFileKey;
	} catch (error) {
		console.error('Error downloading attachment URL for fileKey:', fileKey, error);
		throw error;
	}
};
